import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { IzzyAutomationRuleEntity, IzzyMetadataEngine } from 'mj_generatedentities';
import { mjBizAppsCommonPersonEntity } from '@mj-biz-apps/common-entities';
import { IAdminPanel, AdminPanelStatus } from '../admin-panel.interface';

/** Trigger events offered in the UI — those the engine actually raises today are marked wired. */
const TRIGGER_EVENTS = [
  { value: 'issue.created', label: 'Ticket created', wired: true },
  { value: 'issue.escalated', label: 'Ticket escalated', wired: true },
  { value: 'sla.breached', label: 'SLA breached', wired: true },
  { value: 'issue.status_changed', label: 'Status changed', wired: true },
  { value: 'issue.assigned', label: 'Assignee changed', wired: true },
  { value: 'issue.replied', label: 'Reply sent', wired: false },
  { value: 'issue.updated', label: 'Any field changed', wired: false },
  { value: 'sla.warning', label: 'SLA warning', wired: false },
  { value: 'csat.received', label: 'CSAT received', wired: false },
] as const;

const CONDITION_FIELDS = [
  { value: 'issue.title', label: 'Ticket title' },
  { value: 'issue.description', label: 'Ticket description' },
  { value: 'issue.type', label: 'Ticket type' },
  { value: 'issue.priority', label: 'Ticket priority' },
  { value: 'issue.severity', label: 'Ticket severity' },
  { value: 'issue.status', label: 'Ticket status' },
  { value: 'issue.requester.organization.planLevel', label: 'Requester plan level' },
] as const;

const CONDITION_OPS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'in', label: 'is one of (comma-separated)' },
  { value: 'not_in', label: 'is not one of' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: '>=', label: '≥' },
  { value: '<=', label: '≤' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
] as const;

/** Action types the engine implements today (others are skipped, so we don't offer them). */
const ACTION_TYPES = [
  { value: 'set_field', label: 'Set field' },
  { value: 'transition_status', label: 'Transition status' },
  { value: 'assign_agent', label: 'Assign agent' },
  { value: 'add_comment', label: 'Add internal comment' },
] as const;

interface ConditionRow { field: string; op: string; value: string; }
/** One action row in the builder; only the fields relevant to its type are used on save. */
interface ActionRow { type: string; field: string; value: string; personId: string; content: string; }

/** The entity's strict TriggerEvent union (generated from the DB CHECK constraint). */
type TriggerEventValue = IzzyAutomationRuleEntity['TriggerEvent'];

interface RuleForm {
  name: string;
  description: string;
  triggerEvent: TriggerEventValue;
  sequence: number;
  isActive: boolean;
  operator: 'AND' | 'OR';
  conditions: ConditionRow[];
  actions: ActionRow[];
}

interface RuleDisplay {
  rule: IzzyAutomationRuleEntity;
  isEditing: boolean;
  form: RuleForm;
}

/**
 * Automation Rules admin panel (PRD v4.0 §12). Org-scoped CRUD over event→condition→action rules the
 * AutomationRuleService runs on ticket lifecycle events. Presents Conditions/Actions JSON as builders
 * and serializes to the engine's shape on save. Mirrors the Assignment Rules panel.
 */
@Component({
  standalone: false,
  selector: 'izzy-automation-rules',
  templateUrl: './automation-rules.component.html',
  styleUrls: ['./automation-rules.component.css'],
})
export class AutomationRulesComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() organizationId: string | undefined;
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();

  protected rules: RuleDisplay[] = [];

  /** Org members offered in the "assign agent" action picker (id + display name). */
  protected assignablePeople: { id: string; name: string }[] = [];

  protected readonly triggers = TRIGGER_EVENTS;
  protected readonly fields = CONDITION_FIELDS;
  protected readonly ops = CONDITION_OPS;
  protected readonly actionTypes = ACTION_TYPES;

  protected isLoading = true;
  protected isSaving = false;
  protected errorMessage = '';
  protected successMessage = '';

  protected isCreating = false;
  protected createForm: RuleForm = this.emptyForm();

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private emptyForm(): RuleForm {
    return {
      name: '',
      description: '',
      triggerEvent: 'issue.created',
      sequence: 100,
      isActive: true,
      operator: 'AND',
      conditions: [],
      actions: [{ type: 'set_field', field: 'Priority', value: '', personId: '', content: '' }],
    };
  }

  protected async loadData(): Promise<void> {
    if (!this.organizationId) {
      this.rules = [];
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    try {
      await this.loadAssignablePeople();
      const rv = new RunView();
      const res = await rv.RunView<IzzyAutomationRuleEntity>({
        EntityName: 'Automation Rules',
        ExtraFilter: `OrganizationID = '${this.organizationId}'`,
        OrderBy: 'TriggerEvent ASC, Sequence ASC, Name ASC',
        ResultType: 'entity_object',
      });
      const rules = res.Success ? res.Results : [];
      this.rules = rules.map(r => ({ rule: r, isEditing: false, form: this.toForm(r) }));

      this.statusChange.emit(
        this.rules.length === 0
          ? { status: 'none', tooltip: 'No automation rules' }
          : { status: 'good', tooltip: `${this.rules.filter(r => r.rule.IsActive).length} active rule(s)` }
      );
    } catch (error) {
      console.error('AutomationRules: failed to load', error);
      this.errorMessage = 'Failed to load automation rules.';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Load the org's members for the "assign agent" action dropdown — mirrors the ticket-detail
   * assignee picker: the engine's in-memory OrganizationContacts filtered to this org + active
   * membership, resolved to display names. Any staff member can be assigned (no role gate).
   */
  private async loadAssignablePeople(): Promise<void> {
    try {
      if (!this.organizationId) { this.assignablePeople = []; return; }
      const orgContacts = IzzyMetadataEngine.Instance.OrganizationContacts
        .filter(oc => oc.OrganizationID === this.organizationId && IzzyMetadataEngine.Instance.IsActiveMembership(oc));
      const personIds = Array.from(new Set(
        orgContacts.map(oc => oc.PersonID).filter((id): id is string => !!id)
      ));
      if (personIds.length === 0) { this.assignablePeople = []; return; }

      const esc = (s: string) => s.replace(/'/g, "''");
      const rv = new RunView();
      const peopleRes = await rv.RunView<mjBizAppsCommonPersonEntity>({
        EntityName: 'MJ_BizApps_Common: People',
        ExtraFilter: `ID IN (${personIds.map(id => `'${esc(id)}'`).join(', ')})`,
        OrderBy: 'LastName ASC',
        ResultType: 'entity_object',
      });
      this.assignablePeople = (peopleRes.Success ? peopleRes.Results : [])
        .map(p => ({ id: p.ID, name: p.DisplayName || `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.Email || 'Unknown' }));
    } catch (error) {
      console.error('AutomationRules: loadAssignablePeople failed', error);
      this.assignablePeople = [];
    }
  }

  private toForm(r: IzzyAutomationRuleEntity): RuleForm {
    let operator: 'AND' | 'OR' = 'AND';
    let conditions: ConditionRow[] = [];
    try {
      const c = JSON.parse(r.Conditions || '{}');
      operator = (c.operator || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND';
      conditions = Array.isArray(c.rules)
        ? c.rules.map((l: { field?: string; op?: string; value?: unknown }) => ({
            field: l.field || 'issue.priority',
            op: l.op || 'equals',
            value: Array.isArray(l.value) ? l.value.join(', ') : (l.value == null ? '' : String(l.value)),
          }))
        : [];
    } catch { /* malformed → empty */ }

    let actions: ActionRow[] = [];
    try {
      const a = JSON.parse(r.Actions || '{}');
      actions = Array.isArray(a.actions)
        ? a.actions.map((x: { type?: string; field?: string; value?: unknown; personId?: string; content?: string }) => ({
            type: x.type || 'set_field',
            field: x.field || '',
            value: x.value == null ? '' : String(x.value),
            personId: x.personId || '',
            content: x.content || '',
          }))
        : [];
    } catch { /* malformed → empty */ }
    if (actions.length === 0) {
      actions = [{ type: 'set_field', field: 'Priority', value: '', personId: '', content: '' }];
    }

    return {
      name: r.Name,
      description: r.Description || '',
      triggerEvent: r.TriggerEvent,
      sequence: r.Sequence,
      isActive: !!r.IsActive,
      operator,
      conditions,
      actions,
    };
  }

  private buildConditionsJson(form: RuleForm): string {
    const rules = form.conditions
      .filter(c => c.field && c.op)
      .map(c => ({
        field: c.field,
        op: c.op,
        value: c.op === 'in' || c.op === 'not_in'
          ? c.value.split(',').map(v => v.trim()).filter(Boolean)
          : c.value,
      }));
    return JSON.stringify({ operator: form.operator, rules });
  }

  private buildActionsJson(form: RuleForm): string {
    const actions = form.actions
      .filter(a => a.type)
      .map(a => {
        switch (a.type) {
          case 'set_field': return { type: 'set_field', field: a.field, value: a.value };
          case 'transition_status': return { type: 'transition_status', value: a.value };
          case 'assign_agent': return { type: 'assign_agent', personId: a.personId };
          case 'add_comment': return { type: 'add_comment', content: a.content, isInternal: true };
          default: return { type: a.type };
        }
      });
    return JSON.stringify({ actions });
  }

  // ========== Display helpers ==========

  triggerLabel(value: string): string {
    return TRIGGER_EVENTS.find(t => t.value === value)?.label || value;
  }

  actionsSummary(d: RuleDisplay): string {
    return d.form.actions.map(a => this.actionTypes.find(t => t.value === a.type)?.label || a.type).join(', ') || '—';
  }

  // ========== Condition + action rows ==========

  addCondition(form: RuleForm): void {
    form.conditions.push({ field: 'issue.priority', op: 'equals', value: '' });
  }
  removeCondition(form: RuleForm, i: number): void {
    form.conditions.splice(i, 1);
  }
  addAction(form: RuleForm): void {
    form.actions.push({ type: 'set_field', field: 'Priority', value: '', personId: '', content: '' });
  }
  removeAction(form: RuleForm, i: number): void {
    form.actions.splice(i, 1);
    if (form.actions.length === 0) {
      form.actions.push({ type: 'set_field', field: 'Priority', value: '', personId: '', content: '' });
    }
  }

  // ========== Create ==========

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.emptyForm();
  }
  cancelCreate(): void {
    this.isCreating = false;
  }
  async saveCreate(): Promise<void> {
    if (!this.validate(this.createForm)) return;
    if (!this.organizationId) return;
    this.isSaving = true;
    try {
      const md = new Metadata();
      const r = await md.GetEntityObject<IzzyAutomationRuleEntity>('Automation Rules');
      r.NewRecord();
      r.OrganizationID = this.organizationId;
      this.applyForm(r, this.createForm);
      if (!await r.Save()) {
        this.errorMessage = r.LatestResult?.CompleteMessage || 'Failed to create rule.';
        return;
      }
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Automation rule created');
    } catch (error) {
      console.error('AutomationRules: create failed', error);
      this.errorMessage = 'Failed to create rule.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Edit ==========

  startEdit(d: RuleDisplay): void {
    d.isEditing = true;
    d.form = this.toForm(d.rule);
  }
  cancelEdit(d: RuleDisplay): void {
    d.isEditing = false;
  }
  async saveEdit(d: RuleDisplay): Promise<void> {
    if (!this.validate(d.form)) return;
    this.isSaving = true;
    try {
      this.applyForm(d.rule, d.form);
      if (!await d.rule.Save()) {
        this.errorMessage = d.rule.LatestResult?.CompleteMessage || 'Failed to update rule.';
        return;
      }
      d.isEditing = false;
      this.showSuccess('Automation rule updated');
    } catch (error) {
      console.error('AutomationRules: edit failed', error);
      this.errorMessage = 'Failed to update rule.';
    } finally {
      this.isSaving = false;
    }
  }
  async confirmDelete(d: RuleDisplay): Promise<void> {
    this.isSaving = true;
    try {
      if (!await d.rule.Delete()) {
        this.errorMessage = 'Failed to delete rule.';
        return;
      }
      this.rules = this.rules.filter(x => x !== d);
      this.showSuccess('Automation rule deleted');
    } catch (error) {
      console.error('AutomationRules: delete failed', error);
      this.errorMessage = 'Failed to delete rule.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Shared ==========

  private validate(form: RuleForm): boolean {
    if (!form.name.trim()) { this.errorMessage = 'Rule name is required.'; return false; }
    if (!form.triggerEvent) { this.errorMessage = 'Choose a trigger event.'; return false; }
    const realActions = form.actions.filter(a => {
      switch (a.type) {
        case 'set_field': return a.field && a.value.trim();
        case 'transition_status': return a.value.trim();
        case 'assign_agent': return a.personId.trim();
        case 'add_comment': return a.content.trim();
        default: return false;
      }
    });
    if (realActions.length === 0) {
      this.errorMessage = 'Add at least one complete action.';
      return false;
    }
    this.errorMessage = '';
    return true;
  }

  private applyForm(r: IzzyAutomationRuleEntity, form: RuleForm): void {
    r.Name = form.name.trim();
    r.Description = form.description.trim() || null;
    r.TriggerEvent = form.triggerEvent;
    r.Sequence = form.sequence ?? 100;
    r.IsActive = form.isActive;
    r.Conditions = this.buildConditionsJson(form);
    r.Actions = this.buildActionsJson(form);
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadAutomationRulesComponent(): void {}
